import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';
import {
  createPreference,
  searchPreferences,
  updatePreference,
  getPreference,
  deletePreference,
  recoverPreference
} from 'apis/up';
import { ActionTypes, localChartPlaceholder } from './constants';
import { selectUserPreferenceList } from './selectors';
import { up } from './migrations';

export const addUserPreference = createAction(ActionTypes.ADD);
export const updateUserPreference = createAction(ActionTypes.UPDATE);
export const removeUserPreference = createAction(ActionTypes.REMOVE);
const requestUserPreferences = createAction(ActionTypes.REQUEST);
const successUserPreferences = createAction(ActionTypes.SUCCESS);
const failureUserPreferences = createAction(ActionTypes.FAILURE);

function createAction(type) {
  return (payload, meta = {}) => ({ type, payload, meta });
}

export function getUserPreference({ preferenceId, category }) {
  return async (dispatch) => {
    const preference = await getPreference({ preferenceId });

    dispatch(addUserPreference(preference, { preferenceId, category }));

    return preference;
  };
}

export function getUserPreferenceList({ category } = {}) {
  return async (dispatch) => {
    dispatch(requestUserPreferences(null, { category }));

    try {
      let preferences = await searchPreferences({ category });
      preferences = preferences.map((preference) => up(preference));

      dispatch(successUserPreferences(preferences, { category }));
    } catch (error) {
      logger.error(error);
      dispatch(failureUserPreferences({ name: error.name, message: error.message }, { category }));
    }
  };
}

export function createUserPreference(data) {
  return async (dispatch) => {
    const { category } = data;
    const { preferenceId, message } = await createPreference(data);

    if (message !== 'success') {
      throw new Error('Failed to create user preference');
    }

    return dispatch(getUserPreference({ preferenceId, category }));
  };
}

export function patchUserPreference({ preferenceId, category, ...rest }) {
  return async (dispatch) => {
    const { message } = await updatePreference({ preferenceId, ...rest });

    if (message !== 'success') {
      throw new Error('Failed to update user preference');
    }

    dispatch(updateUserPreference(rest, { preferenceId, category }));
  };
}

export function deleteUserPreference({ preferenceId, category, default: _default }) {
  return async (dispatch) => {
    const { message } = await deletePreference({ preferenceId });

    if (message !== 'success') {
      throw new Error('Failed to delete user preference');
    }

    dispatch(removeUserPreference({ default: _default }, { preferenceId, category }));
  };
}

export function recoverUserPreference({ preferenceId, category }) {
  return async (dispatch) => {
    const { message } = await recoverPreference({ preferenceId });

    if (message !== 'success') {
      throw new Error('Failed to recover user preference');
    }

    return dispatch(getUserPreference({ preferenceId, category }));
  };
}

export function deleteChartPreference(preference) {
  return async (dispatch, getState) => {
    const chartSetPreferences = selectUserPreferenceList(
      getState().data.userPreferences,
      USER_PREFERENCE_SETS.CHART_SET
    );
    const isDeletedChart = (chart) => chart?.preferenceId === preference.preferenceId;
    const patchedPreferences = chartSetPreferences.reduce((acc, chartSetPreference) => {
      const { preferenceId, category, state = {} } = chartSetPreference;
      const { charts = [] } = state;

      if (charts.some(isDeletedChart)) {
        acc.push({
          preferenceId,
          category,
          state: {
            ...state,
            charts: charts.map((chart) =>
              isDeletedChart(chart)
                ? {
                    preferenceId: localChartPlaceholder.preferenceId,
                    name: localChartPlaceholder.name,
                    category: localChartPlaceholder.category,
                    local: localChartPlaceholder.local
                  }
                : chart
            )
          }
        });
      }

      return acc;
    }, []);

    for (let patchedPreference of patchedPreferences) {
      try {
        await dispatch(patchUserPreference(patchedPreference));
      } catch (error) {
        logger.error(error);
      }
    }

    return dispatch(deleteUserPreference(preference));
  };
}
